import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, CreditCard, Euro, Gift, Users, Zap, Shield, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';

const Donate = () => {
  const [donationAmount, setDonationAmount] = useState(50);
  const [donationType, setDonationType] = useState('unique');
  const [customAmount, setCustomAmount] = useState('');

  const predefinedAmounts = [25, 50, 100, 200];

  const donationImpacts = [
    {
      amount: 25,
      impact: 'Nourrit une famille pendant 1 semaine',
      icon: '🍽️'
    },
    {
      amount: 50,
      impact: 'Finance 1 mois d\'école pour un enfant',
      icon: '📚'
    },
    {
      amount: 100,
      impact: 'Fournit de l\'eau potable à 10 personnes pour 1 mois',
      icon: '💧'
    },
    {
      amount: 200,
      impact: 'Équipe un dispensaire en médicaments de base',
      icon: '⚕️'
    }
  ];

  const projectsToSupport = [
    {
      id: 'education',
      name: 'Éducation pour tous',
      description: 'Construction d\'écoles et formation d\'enseignants',
      progress: 75,
      goal: '100,000€',
      raised: '75,000€',
      color: 'bg-primary'
    },
    {
      id: 'health',
      name: 'Santé communautaire',
      description: 'Centres de santé et campagnes de vaccination',
      progress: 60,
      goal: '80,000€',
      raised: '48,000€',
      color: 'bg-secondary'
    },
    {
      id: 'water',
      name: 'Accès à l\'eau',
      description: 'Puits et systèmes d\'assainissement',
      progress: 40,
      goal: '150,000€',
      raised: '60,000€',
      color: 'bg-accent'
    }
  ];

  const features = [
    {
      icon: Shield,
      title: 'Sécurisé',
      description: 'Paiements cryptés et conformes'
    },
    {
      icon: CheckCircle,
      title: 'Transparent',
      description: 'Suivi détaillé de vos dons'
    },
    {
      icon: Zap,
      title: 'Impact immédiat',
      description: 'Fonds rapidement déployés'
    }
  ];

  const getCurrentImpact = () => {
    const amount = customAmount ? parseInt(customAmount) : donationAmount;
    return donationImpacts.find(impact => impact.amount <= amount) || donationImpacts[0];
  };

  return (
    <>
      <div className="relative z-10 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-4">Faire un Don</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Chaque don compte et contribue directement à nos missions humanitaires. 
            Ensemble, créons un impact durable pour les communautés dans le besoin.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Donation Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Donation Type */}
            <Card className="border-border/50">
              <CardHeader>
                <h3 className="text-xl font-semibold">Type de don</h3>
              </CardHeader>
              <CardContent>
                <RadioGroup value={donationType} onValueChange={setDonationType}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2 p-4 border border-border rounded-lg hover:border-primary/50 transition-colors">
                      <RadioGroupItem value="unique" id="unique" />
                      <Label htmlFor="unique" className="flex-1 cursor-pointer">
                        <div className="flex items-center space-x-2">
                          <Gift className="h-5 w-5 text-primary" />
                          <div>
                            <div className="font-medium">Don ponctuel</div>
                            <div className="text-sm text-muted-foreground">Un don unique</div>
                          </div>
                        </div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-4 border border-border rounded-lg hover:border-primary/50 transition-colors">
                      <RadioGroupItem value="monthly" id="monthly" />
                      <Label htmlFor="monthly" className="flex-1 cursor-pointer">
                        <div className="flex items-center space-x-2">
                          <Users className="h-5 w-5 text-secondary" />
                          <div>
                            <div className="font-medium">Don mensuel</div>
                            <div className="text-sm text-muted-foreground">Impact continu</div>
                          </div>
                        </div>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Amount Selection */}
            <Card className="border-border/50">
              <CardHeader>
                <h3 className="text-xl font-semibold">Montant du don</h3>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {predefinedAmounts.map((amount) => (
                    <Button
                      key={amount}
                      variant={donationAmount === amount && !customAmount ? "default" : "outline"}
                      onClick={() => {
                        setDonationAmount(amount);
                        setCustomAmount('');
                      }}
                      className="h-12"
                    >
                      {amount}€
                    </Button>
                  ))}
                </div>
                
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    type="number"
                    placeholder="Montant personnalisé"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setDonationAmount(0);
                    }}
                    className="pl-10"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Project Selection */}
            <Card className="border-border/50">
              <CardHeader>
                <h3 className="text-xl font-semibold">Choisir un projet (optionnel)</h3>
                <p className="text-muted-foreground text-sm">
                  Vous pouvez affecter votre don à un projet spécifique ou laisser PADAID l'utiliser là où c'est le plus nécessaire.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 border border-border rounded-lg hover:border-accent/50 transition-colors cursor-pointer">
                  <div className="flex items-center space-x-2 mb-2">
                    <input type="radio" name="project" id="general" defaultChecked />
                    <Label htmlFor="general" className="font-medium cursor-pointer">
                      Fonds général - Là où c'est le plus nécessaire
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground pl-6">
                    PADAID utilise votre don pour les besoins les plus urgents
                  </p>
                </div>

                {projectsToSupport.map((project) => (
                  <div key={project.id} className="p-4 border border-border rounded-lg hover:border-primary/50 transition-colors cursor-pointer">
                    <div className="flex items-center space-x-2 mb-3">
                      <input type="radio" name="project" id={project.id} />
                      <Label htmlFor={project.id} className="font-medium cursor-pointer">
                        {project.name}
                      </Label>
                      <Badge variant="outline" className="text-xs">
                        {project.progress}%
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3 pl-6">
                      {project.description}
                    </p>
                    <div className="pl-6">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Collecté: {project.raised}</span>
                        <span className="text-muted-foreground">Objectif: {project.goal}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${project.color}`}
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Summary & Payment */}
          <div className="space-y-6">
            {/* Impact Preview */}
            <Card className="border-border/50 bg-gradient-to-br from-primary/5 to-secondary/5">
              <CardHeader>
                <h3 className="text-xl font-semibold flex items-center">
                  <Heart className="h-5 w-5 mr-2 text-primary" />
                  Votre Impact
                </h3>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">
                    {customAmount || donationAmount}€
                  </div>
                  <div className="text-sm text-muted-foreground mb-4">
                    {donationType === 'monthly' ? 'par mois' : 'don unique'}
                  </div>
                  
                  {getCurrentImpact() && (
                    <div className="bg-background/80 rounded-lg p-4">
                      <div className="text-2xl mb-2">{getCurrentImpact().icon}</div>
                      <p className="text-sm font-medium">{getCurrentImpact().impact}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Payment Button */}
            <Card className="border-border/50">
              <CardContent className="p-6">
                <Button size="lg" className="w-full bg-primary hover:bg-primary/90 mb-4" asChild>
                  <Link to="/payment-v2?package=donation_custom">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Donner maintenant
                  </Link>
                </Button>
                
                <div className="space-y-3">
                  {features.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <div key={index} className="flex items-center space-x-2">
                        <Icon className="h-4 w-4 text-primary" />
                        <div>
                          <div className="text-sm font-medium">{feature.title}</div>
                          <div className="text-xs text-muted-foreground">{feature.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <p className="text-xs text-muted-foreground mt-4">
                  Vos dons sont déductibles fiscalement à hauteur de 66% dans la limite de 20% de votre revenu imposable.
                </p>
              </CardContent>
            </Card>

            {/* Quick Impacts */}
            <Card className="border-border/50">
              <CardHeader>
                <h4 className="font-semibold">Exemples d'impact</h4>
              </CardHeader>
              <CardContent className="space-y-3">
                {donationImpacts.map((impact, index) => (
                  <div key={index} className="flex items-center space-x-3 text-sm">
                    <span className="text-lg">{impact.icon}</span>
                    <div>
                      <span className="font-medium text-primary">{impact.amount}€</span>
                      <span className="text-muted-foreground"> → {impact.impact}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="bg-muted/50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Pourquoi nous faire confiance ?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div>
              <div className="text-3xl font-bold text-primary mb-2">85%</div>
              <p className="text-sm text-muted-foreground">des dons vont directement aux bénéficiaires</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-secondary mb-2">100%</div>
              <p className="text-sm text-muted-foreground">de transparence dans nos comptes</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-accent mb-2">15 ans</div>
              <p className="text-sm text-muted-foreground">d'expérience humanitaire</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Donate;